import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  generateCollectionBuildLooks,
  generateCollectionBuildMoodboard,
  getCollectionBuildErrorMessage,
  getCollectionBuildReportPagesCatalog,
  getCollectionBuildSession,
  CollectionBuildLookPayload,
  CollectionBuildReportPagesCatalog,
  CollectionBuildSessionPayload,
} from "../../app/api/collectionBuild";
import {
  getImageGeneratorSurfacesCatalog,
  ImageGeneratorCatalogSurfaceSlide,
} from "../../app/api/imageGenerator";
import { RootState } from "../../app/store";
import {
  CATEGORIES,
  MARKETS,
} from "../../constants/imageGeneratorDictionaries";
import CollectionBuildDirections from "./CollectionBuildDirections";
import CollectionBuildRangePlan from "./CollectionBuildRangePlan";
import CollectionBuildResults, {
  CollectionBuildResultsBoundary,
} from "./CollectionBuildResults";
import CollectionBuildSelect from "./CollectionBuildSelect";
import CollectionBuildSilhouette from "./CollectionBuildSilhouette";
import CollectionBuildTracker from "./CollectionBuildTracker";
import { useCollectionBuildSessions } from "./CollectionBuildSessionsContext";
import {
  colourStoriesToPalettes,
  denimPaletteOptions,
  catalogMacroMicroThemes,
  catalogMicroThemeLabel,
  catalogPatternStoriesForMicro,
  catalogPatternTrendLabel,
  catalogPatternTrendPreferenceGroups,
  enrichCollectionBuildDetails,
  flattenCatalogPatternTrends,
  flattenCatalogSketchAssets,
  hydrateMicroAndStorySelection,
  humanizeCollectionThemeId,
  preferenceOptionsForDetails,
  reportSlidesFromPages,
  reportSlidesForMacroThemeColour,
  reportSlidesForMacroThemePattern,
  reportSlidesForMicroThemePattern,
  denimTilesFromStyleSelections,
  resolveDenimWashDisplay,
  sampleMultiSelectIds,
} from "./collectionBuildCatalog";
import {
  CollectionBuildResultDetails,
  COLLECTION_BUILD_PATTERN_TREND_IDS_MAX,
  COLLECTION_BUILD_SELECTED_ASSET_STEMS_MAX,
  COLLECTION_DIRECTION_OPTIONS,
  ColorPaletteOption,
  LOCKED_GENRE,
  MAX_QUANTITY,
  MAX_RANGE_PLAN_ROWS,
  MAX_RANGE_PRODUCTS,
  NO_STARTING_DIRECTIONS_MESSAGE,
  PatternTrendChoice,
  RangePlanItem,
  SilhouetteGroup,
  StartingDirection,
  TrendSourceChoice,
  areSilhouetteGroupsComplete,
  hasIncompleteSilhouetteUpload,
  INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE,
  availableCollectionDirections,
  denimSessionIdsFromGroups,
  garmentFormatFallbackFromGroups,
  categoryLabel,
  collectionBuildBorderClassName,
  createRangePlanItem,
  directionSkipsMacroTheme,
  isCollectionBuildDenimEligibleCategory,
  fieldLabelClassName,
  FIELD_CONTROL_WIDTH_CLASS,
  fieldsRowClassName,
  formFieldsShellClassName,
  outlineButtonClassName,
  pageShellClassName,
  patternTrendLabel,
  productOptionsFor,
  rangePlanBreakdown,
  RequiredMark,
  scrollCollectionSectionIntoView,
  sectionBlockClassName,
  sectionTitleClassName,
  SKIP_MOODBOARD_CONFIRM_BODY,
  SKIP_MOODBOARD_CONFIRM_TITLE,
  snapshotCollectionBuildDetails,
  submitButtonClassName,
  sumRangePlanQuantity,
  toSelectOptions,
} from "./collectionBuildConstants";
import {
  clearCollectionBuildDraftSessionId,
  clearMistakenImageGeneratorPendingId,
  collectionBuildSessionSearchParams,
  collectionBuildSummaryTitle,
  collectionBuildThemeLabelFromFields,
  COLLECTION_BUILD_SESSION_PARAM_LEGACY,
  collectionSessionHasOutputs,
  detailsFromCollectionSession,
  ensureCollectionBuildSessionId,
  formHydrationFromCollectionSession,
  looksPayloadFromGroups,
  mergeCollectionBuildDetailsPreferences,
  persistCollectionBuildSession,
  preferencesFromCollectionRecords,
  readCollectionBuildSessionParam,
  readLegacyCollectionBuildSessionParam,
  rememberCollectionBuildDraftSessionId,
  peekCollectionBuildDraftSessionId,
  sanitizeCollectionBuildSessionPayload,
  collectionBuildGenerationErrorCopy,
  type CollectionBuildFormHydration,
} from "./collectionBuildSession";
import { getGenerativeAssetPreview } from "./shared/generativeCatalog";
import {
  useGenerativeCatalog,
  useGenerativeDenimCatalog,
} from "./shared/useGenerativeCatalog";
import { useGenerativeDenimPreviews } from "./shared/useGenerativeDenimPreviews";

type Props = {
  isSidebarOpen: boolean;
};

const CollectionBuild: React.FC<Props> = ({ isSidebarOpen }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.userId || ""
  );
  const {
    activeSession,
    watchSession,
    loadSession,
    openSession,
    refreshSummaries,
    upsertSummaryFields,
    newDraftNonce,
  } = useCollectionBuildSessions();
  const sessionParam = readCollectionBuildSessionParam(searchParams);
  const legacySessionParam =
    readLegacyCollectionBuildSessionParam(searchParams);
  const [activeTab, setActiveTab] = useState<"query" | "tracker">("query");
  const [market, setMarket] = useState("");
  const [category, setCategory] = useState("");
  const [seasonId, setSeasonId] = useState("");
  const [rangePlan, setRangePlan] = useState<RangePlanItem[]>([]);
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [startingDirection, setStartingDirection] = useState<
    StartingDirection | ""
  >("");
  const [macroTheme, setMacroTheme] = useState("");
  const [selectedPaletteIds, setSelectedPaletteIds] = useState<string[]>([]);
  const [extraPalettes, setExtraPalettes] = useState<ColorPaletteOption[]>([]);
  const [denimMaterialId, setDenimMaterialId] = useState("");
  const [denimWashId, setDenimWashId] = useState("");
  const [patternChoice, setPatternChoice] = useState<PatternTrendChoice | "">(
    ""
  );
  const [trendSource, setTrendSource] = useState<TrendSourceChoice | "">("");
  const [selectedSurfaceIds, setSelectedSurfaceIds] = useState<string[]>([]);
  const [surfaceSlides, setSurfaceSlides] = useState<
    ImageGeneratorCatalogSurfaceSlide[]
  >([]);
  const [surfacesLoading, setSurfacesLoading] = useState(false);
  /** Editorial report pages for View report (macro decks + colour/micro item URLs). */
  const [reportPagesCatalog, setReportPagesCatalog] =
    useState<CollectionBuildReportPagesCatalog | null>(null);
  /** Western micro-theme multi-select (UI); parents recovered from story ids on hydrate. */
  const [selectedMicroThemes, setSelectedMicroThemes] = useState<string[]>([]);
  /** Theme whose optional preference tiles are currently shown. */
  const [activeMicroTheme, setActiveMicroTheme] = useState("");
  /** Pattern Story multi-select — persisted as session `patternTrendIds` (not micro ids). */
  const [selectedPatternStoryIds, setSelectedPatternStoryIds] = useState<
    string[]
  >([]);
  /** Story whose optional preference tiles are currently shown. */
  const [activePatternStoryId, setActivePatternStoryId] = useState("");
  const [selectedPreferenceIds, setSelectedPreferenceIds] = useState<string[]>(
    []
  );
  const [preferencePreviews, setPreferencePreviews] = useState<
    Record<string, string>
  >({});
  const [garmentFormat, setGarmentFormat] = useState("");
  const [embroideryType, setEmbroideryType] = useState("");
  const [patternFormat, setPatternFormat] = useState("");
  const [confirmSkipMoodboard, setConfirmSkipMoodboard] = useState(false);
  const [skipSilhouetteOpen, setSkipSilhouetteOpen] = useState(false);
  const [pendingSkipDetails, setPendingSkipDetails] =
    useState<CollectionBuildResultDetails | null>(null);
  const [lookGroups, setLookGroups] = useState<SilhouetteGroup[] | undefined>();
  /** Live Look Details groups while editing the query form (before generate). */
  const [draftLookGroups, setDraftLookGroups] = useState<SilhouetteGroup[]>([]);
  /** Hydrated look payloads — applied once into Silhouette; edits live in draftLookGroups. */
  const [seedLooks, setSeedLooks] = useState<CollectionBuildLookPayload[]>([]);
  const [resultDetails, setResultDetails] =
    useState<CollectionBuildResultDetails | null>(null);
  const [moodboardSubmitting, setMoodboardSubmitting] = useState(false);
  const [looksSubmitting, setLooksSubmitting] = useState(false);
  /** Stay on the query form after Back to query even if the session has outputs. */
  const [stayOnQuery, setStayOnQuery] = useState(false);
  const [enqueueError, setEnqueueError] = useState<string | null>(null);
  /** True while opening a cbSession until draft form hydrate finishes (or results bind). */
  const [isHydratingSession, setIsHydratingSession] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const lookDetailsRef = useRef<HTMLElement>(null);
  const assetPreviewCache = useRef<Map<string, string>>(new Map());
  const prevSessionParamRef = useRef<number | undefined>();
  const lastHandledNewDraftNonceRef = useRef(0);
  const hydratedFormSessionIdRef = useRef<number | null>(null);
  const pendingFormHydrationRef = useRef<CollectionBuildFormHydration | null>(
    null
  );
  const pendingFormSessionIdRef = useRef<number | null>(null);
  /** Last progress key we persisted (or adopted as hydrate baseline). */
  const lastPersistedProgressKeyRef = useRef<string>("");
  /** After draft hydrate, adopt the next progress key as baseline — no PATCH. */
  const adoptHydrateBaselineRef = useRef(false);
  /** Skip identical sanitized payloads (catalog remap / effect churn). */
  const lastPersistedPayloadJsonRef = useRef<string>("");
  const remapPatternSelectionRef = useRef(false);

  const seasonEnabled = Boolean(market && category);
  const hasRangePlanQuantity = sumRangePlanQuantity(rangePlan) > 0;
  // Prefetch report catalog once range plan has quantity (Look Details follows).
  const catalogNeeded = Boolean(seasonId && hasRangePlanQuantity);
  const resultPersist = resultDetails?.persistPayload as
    | CollectionBuildSessionPayload
    | undefined;
  // Results / Recents reopen may clear form season+range; still need catalog to
  // resolve preference asset urls for Design Details.
  const catalogMarket =
    market || resultPersist?.market || resultDetails?.market || "";
  const catalogCategory = category || resultPersist?.category || "";
  const catalogSeasonId = seasonId || resultPersist?.seasonId || "";
  const catalogSeasonHint =
    resultPersist?.season || resultDetails?.season || "";
  const catalogNeededForResults = Boolean(
    resultDetails &&
      catalogMarket &&
      catalogCategory &&
      (catalogSeasonId || catalogSeasonHint)
  );

  const {
    catalog,
    catalogLoading,
    catalogError,
    seasons,
    seasonsLoading,
    seasonsError,
    seasonLabel,
    hasReportThemes,
    hasCapsules,
  } = useGenerativeCatalog({
    market: catalogMarket,
    category: catalogCategory,
    seasonId: catalogSeasonId,
    genre: LOCKED_GENRE,
    seasonLabelHint: catalogSeasonHint,
    catalogEnabled: catalogNeeded || catalogNeededForResults,
  });
  const denimCatalog = useGenerativeDenimCatalog({
    notifyOnError: true,
    // Fetch for Denim query path, and while viewing Denim results (form may be cleared).
    enabled: Boolean(
      (seasonId && startingDirection === "Denim") ||
        /denim/i.test(resultDetails?.startingDirection || "")
    ),
  });
  const denimCatalogForDetails = useMemo(
    () => ({
      materials: denimCatalog.materials,
      washes: denimCatalog.washes,
    }),
    [denimCatalog.materials, denimCatalog.washes]
  );
  const isDenimPath = startingDirection === "Denim";
  const isWesternPatternFlow =
    !isDenimPath &&
    startingDirection !== "Capsules" &&
    String(category || "").trim() === "Western RTW";
  const denimPreviews = useGenerativeDenimPreviews(
    [...denimCatalog.materials, ...denimCatalog.washes],
    isDenimPath
  );

  const inputsComplete = Boolean(seasonEnabled && seasonId && seasonLabel);
  const lookFieldContext = useMemo(
    () => ({ category, market, startingDirection }),
    [category, market, startingDirection]
  );
  // Starting Direction waits for Look Details: influence always; style type
  // when from the library; material when that field is shown. Sketch and
  // image upload stay optional.
  const lookDetailsComplete = areSilhouetteGroupsComplete(
    draftLookGroups,
    lookFieldContext
  );
  const showDirection = inputsComplete && hasRangePlanQuantity;
  const productTypeOptions = useMemo(
    () => productOptionsFor(market, category),
    [market, category]
  );
  const canAddProduct = rangePlan.length < MAX_RANGE_PLAN_ROWS;
  const marketOptions = toSelectOptions(MARKETS);
  const categoryOptions = CATEGORIES.map(item => ({
    value: item,
    label: categoryLabel(item),
  }));
  const seasonOptions = seasons.map(item => ({
    value: item.id,
    label: item.label,
  }));
  const productSelectOptions = toSelectOptions(productTypeOptions);
  const catalogMatchesContext = Boolean(
    catalog &&
      (catalog.season.id === seasonId || catalog.season.label === seasonLabel)
  );
  // Denim follows Image Generator: Western RTW only (hidden for Ethnic RTW).
  // Macro stays disabled until themes load; Denim uses the constant denim catalog.
  const directionsSettled = !seasonsLoading;
  const denimAllowed = isCollectionBuildDenimEligibleCategory(category);
  const availableDirections = useMemo(
    () =>
      availableCollectionDirections({
        hasReportThemes:
          !catalogLoading && catalogMatchesContext && hasReportThemes,
        hasCapsules: !catalogLoading && catalogMatchesContext && hasCapsules,
        denimAvailable: denimAllowed,
      }),
    [
      catalogLoading,
      catalogMatchesContext,
      denimAllowed,
      hasCapsules,
      hasReportThemes,
    ]
  );
  const directionOptions = useMemo(
    () =>
      denimAllowed
        ? COLLECTION_DIRECTION_OPTIONS
        : COLLECTION_DIRECTION_OPTIONS.filter(option => option !== "Denim"),
    [denimAllowed]
  );
  const directionMessage =
    catalogNeeded && catalogLoading && availableDirections.length <= 1
      ? "Loading catalog themes…"
      : directionsSettled && availableDirections.length === 0
        ? NO_STARTING_DIRECTIONS_MESSAGE
        : undefined;
  const skipsMacro = directionSkipsMacroTheme(startingDirection);
  const isCapsulesPath = startingDirection === "Capsules";
  const selectedMacro = isCapsulesPath
    ? catalog?.capsules?.find(item => item.id === macroTheme)
    : catalog?.macros.find(item => item.id === macroTheme);
  const selectedMacroReportPages = useMemo(() => {
    if (!macroTheme || !reportPagesCatalog) {
      return undefined;
    }
    const themes = isCapsulesPath
      ? reportPagesCatalog.capsules
      : reportPagesCatalog.macros;
    return themes.find(item => item.id === macroTheme);
  }, [isCapsulesPath, macroTheme, reportPagesCatalog]);
  const catalogPalettes = useMemo(() => {
    if (startingDirection === "Denim") {
      return denimPaletteOptions(denimCatalog);
    }
    return colourStoriesToPalettes(selectedMacro?.colourStories || []);
  }, [denimCatalog, selectedMacro, startingDirection]);
  const selectedPalettes = useMemo(
    () =>
      selectedPaletteIds
        .map(id =>
          [...catalogPalettes, ...extraPalettes].find(
            palette => palette.id === id
          )
        )
        .filter((palette): palette is ColorPaletteOption => Boolean(palette)),
    [catalogPalettes, extraPalettes, selectedPaletteIds]
  );

  const patternTrends = useMemo(() => {
    if (startingDirection === "Denim") {
      return [];
    }
    if (
      startingDirection === "Macro-theme" ||
      startingDirection === "Capsules"
    ) {
      return selectedMacro?.patternTrends || [];
    }
    return flattenCatalogPatternTrends(catalog);
  }, [catalog, selectedMacro, startingDirection]);

  /**
   * Persist all selected Pattern Story ids (not Western micro report ids).
   * Cap at Joi max 12; BE `sampleMicroTargetCount` samples further for prompt.
   */
  const patternTrendIdsForPersist = useMemo(() => {
    if (!selectedPatternStoryIds.length) {
      return undefined;
    }
    return sampleMultiSelectIds(
      selectedPatternStoryIds,
      COLLECTION_BUILD_PATTERN_TREND_IDS_MAX
    );
  }, [selectedPatternStoryIds]);

  const activePatternStoryIdForPersist =
    activePatternStoryId || selectedPatternStoryIds[0] || "";

  /** Thin preference stems only when over Joi max 8 (BE also caps tiles). */
  const selectedAssetStemsForPersist = useMemo(
    () =>
      sampleMultiSelectIds(
        selectedPreferenceIds,
        COLLECTION_BUILD_SELECTED_ASSET_STEMS_MAX
      ),
    [selectedPreferenceIds]
  );

  const selectedTrendAssets = useMemo(() => {
    const seen = new Set<string>();
    return patternTrends
      .filter(trend => selectedMicroThemes.includes(trend.id))
      .flatMap(trend =>
        (trend.assets || []).filter(asset => {
          if (seen.has(asset.stem)) {
            return false;
          }
          seen.add(asset.stem);
          return true;
        })
      );
  }, [patternTrends, selectedMicroThemes]);

  const activeTrendAssets = useMemo(() => {
    if (
      !isWesternPatternFlow &&
      patternChoice === "choose" &&
      patternTrends.length
    ) {
      const seen = new Set<string>();
      return patternTrends.flatMap(item =>
        (item.assets || []).filter(asset => {
          if (seen.has(asset.stem)) {
            return false;
          }
          seen.add(asset.stem);
          return true;
        })
      );
    }
    // Preference previews for the active Pattern Story (not Western micro ids).
    if (activePatternStoryId) {
      const fromStories = isWesternPatternFlow
        ? catalogPatternStoriesForMicro(selectedMacro, activeMicroTheme)
        : patternTrends;
      const story = fromStories.find(
        item => String(item.id) === String(activePatternStoryId)
      );
      if (story?.assets?.length) {
        return story.assets;
      }
    }
    if (activeMicroTheme) {
      const trend = patternTrends.find(item => item.id === activeMicroTheme);
      if (trend?.assets?.length) {
        return trend.assets;
      }
    }
    if (selectedTrendAssets.length > 0) {
      return selectedTrendAssets;
    }
    return patternTrends[0]?.assets || [];
  }, [
    activeMicroTheme,
    activePatternStoryId,
    isWesternPatternFlow,
    patternChoice,
    patternTrends,
    selectedMacro,
    selectedTrendAssets,
  ]);

  const recentsThemeLabel = collectionBuildThemeLabelFromFields({
    startingDirection,
    macroThemeId: macroTheme,
    macroThemeName: selectedMacro?.name,
    denimWashId,
    denimWashName: resolveDenimWashDisplay(denimWashId, denimCatalog.washes),
  });
  const recentsSessionTitle = collectionBuildSummaryTitle({
    market,
    category,
    season: seasonLabel,
    theme: recentsThemeLabel,
  });

  const buildPersistPayload = useCallback(
    (skipMoodboard = false): CollectionBuildSessionPayload =>
      sanitizeCollectionBuildSessionPayload({
        season: seasonLabel,
        seasonId,
        genre: LOCKED_GENRE,
        market,
        category,
        startingDirection:
          startingDirection === "Denim"
            ? "denim"
            : startingDirection === "Capsules"
              ? "capsules"
              : startingDirection === "Macro-theme"
                ? "macro-theme"
                : "",
        skipReportThemes: startingDirection === "Denim",
        skipMoodboard,
        macroThemeId: macroTheme,
        colourStoryId: selectedPaletteIds[0],
        colourStoryIds: selectedPaletteIds,
        patternTrendId:
          trendSource === "surface"
            ? undefined
            : activePatternStoryIdForPersist ||
              (!isWesternPatternFlow && patternChoice === "recommend"
                ? patternTrends[0]?.id
                : undefined),
        patternTrendIds:
          trendSource === "surface"
            ? undefined
            : patternTrendIdsForPersist ||
              (!isWesternPatternFlow && patternChoice === "recommend"
                ? sampleMultiSelectIds(
                    patternTrends.map(item => item.id).filter(Boolean),
                    COLLECTION_BUILD_PATTERN_TREND_IDS_MAX
                  )
                : undefined),
        surfaceId:
          trendSource === "surface"
            ? selectedSurfaceIds[0] || undefined
            : undefined,
        surfaceIds:
          trendSource === "surface" && selectedSurfaceIds.length
            ? selectedSurfaceIds
            : undefined,
        patternChoice:
          trendSource === "surface" ? "none" : patternChoice || undefined,
        selectedAssetStems:
          trendSource === "surface" ? [] : selectedAssetStemsForPersist,
        palettes: selectedPalettes,
        paletteHexes: selectedPalettes.flatMap(palette => palette.colors),
        // Session-level: first style with a format (BE may still expect one value).
        // Per-style values on `looks` are the UI source of truth.
        ...(() => {
          const fromLooks = garmentFormatFallbackFromGroups(draftLookGroups);
          const format = fromLooks.garmentFormat || garmentFormat;
          const pattern = fromLooks.patternFormat || patternFormat;
          const stitch =
            fromLooks.embroideryType ||
            (format === "Embroidery" ? embroideryType : "");
          return {
            garmentFormat: format,
            patternFormat: pattern,
            embroideryType:
              format === "Embroidery" ? stitch.trim() || undefined : undefined,
          };
        })(),
        ...(() => {
          // Moodboard still needs session denim ids — derive from Look Details.
          const fromStyles = denimSessionIdsFromGroups(draftLookGroups);
          return {
            denimMaterialId: fromStyles.denimMaterialId || denimMaterialId,
            denimWashId: fromStyles.denimWashId || denimWashId,
          };
        })(),
        additionalPrompt: additionalPrompt.trim(),
        themeLabel: recentsThemeLabel || undefined,
        sessionTitle: recentsSessionTitle || undefined,
        looks: looksPayloadFromGroups(draftLookGroups),
        rangePlan: {
          items: rangePlan
            .filter(item => item.productType.trim() && item.quantity > 0)
            .map(item => ({
              productType: item.productType.trim(),
              quantity: item.quantity,
            })),
        },
      }),
    [
      activePatternStoryIdForPersist,
      additionalPrompt,
      category,
      denimMaterialId,
      denimWashId,
      draftLookGroups,
      embroideryType,
      garmentFormat,
      isWesternPatternFlow,
      macroTheme,
      market,
      patternChoice,
      patternFormat,
      patternTrendIdsForPersist,
      patternTrends,
      rangePlan,
      seasonId,
      seasonLabel,
      selectedAssetStemsForPersist,
      selectedPaletteIds,
      selectedPalettes,
      selectedSurfaceIds,
      startingDirection,
      trendSource,
      recentsThemeLabel,
      recentsSessionTitle,
    ]
  );

  const macroOptions = useMemo(
    () =>
      (isCapsulesPath ? catalog?.capsules || [] : catalog?.macros || []).map(
        item => ({
          value: item.id,
          label: item.name,
        })
      ),
    [catalog, isCapsulesPath]
  );

  /** Recents title + order after autosave / enqueue (includes theme/wash when selected). */
  const refreshSummaryFromForm = useCallback(
    (sessionId: number, status?: string) => {
      upsertSummaryFields(sessionId, {
        market: market || null,
        category: category || null,
        season: seasonLabel || null,
        theme: recentsThemeLabel || null,
        status,
        rangePlan: hasRangePlanQuantity
          ? {
              items: rangePlan
                .filter(item => item.productType.trim() && item.quantity > 0)
                .map(item => ({
                  productType: item.productType.trim(),
                  quantity: item.quantity,
                })),
            }
          : null,
      });
    },
    [
      category,
      hasRangePlanQuantity,
      market,
      rangePlan,
      recentsThemeLabel,
      seasonLabel,
      upsertSummaryFields,
    ]
  );

  const catalogMicros = useMemo(
    () => catalogMacroMicroThemes(selectedMacro),
    [selectedMacro]
  );
  const microOptions = useMemo(
    () =>
      catalogMicros.map(item => ({
        value: item.id,
        label: item.name || catalogMicroThemeLabel(catalog, item.id) || item.id,
      })),
    [catalog, catalogMicros]
  );
  const patternStoryTrends = useMemo(() => {
    if (isWesternPatternFlow) {
      return catalogPatternStoriesForMicro(selectedMacro, activeMicroTheme);
    }
    return patternTrends;
  }, [activeMicroTheme, isWesternPatternFlow, patternTrends, selectedMacro]);
  const patternStoryOptions = useMemo(
    () =>
      patternStoryTrends.map(item => ({
        value: item.id,
        label: catalogPatternTrendLabel(item, catalog),
      })),
    [catalog, patternStoryTrends]
  );
  /** Active Pattern Story tiles only — image cards, no captions or group dump. */
  const preferenceGroups = useMemo(() => {
    const story = patternStoryTrends.find(
      trend => String(trend.id) === String(activePatternStoryId)
    );
    if (!story) {
      return [];
    }
    return catalogPatternTrendPreferenceGroups(
      [story],
      preferencePreviews,
      catalog
    ).map(group => ({ ...group, label: "" }));
  }, [activePatternStoryId, catalog, patternStoryTrends, preferencePreviews]);
  const sketchAssets = useMemo(
    () => flattenCatalogSketchAssets(catalog, selectedMacro),
    [catalog, selectedMacro]
  );
  const hasPatternTrends = isWesternPatternFlow
    ? catalogMicros.length > 0 || patternTrends.length > 0
    : patternTrends.length > 0;
  const showMacro =
    showDirection &&
    catalogMatchesContext &&
    ((startingDirection === "Macro-theme" && hasReportThemes) ||
      (startingDirection === "Capsules" && hasCapsules));
  const showPalette =
    startingDirection === "Denim"
      ? showDirection
      : showMacro && Boolean(macroTheme);
  const hasSelectedPalette = selectedPaletteIds.length > 0;
  // Materials / washes live on Look Details per style (not overall Directions).
  const denimSpecsReady = startingDirection !== "Denim" || hasSelectedPalette;
  const reportThemesReady = isCapsulesPath ? hasCapsules : hasReportThemes;
  const isSurfacePath = trendSource === "surface";
  const isPatternPath = trendSource === "pattern";
  const skipPatternSections =
    isDenimPath ||
    (hasSelectedPalette &&
      denimSpecsReady &&
      !catalogLoading &&
      !hasPatternTrends &&
      !isSurfacePath &&
      (skipsMacro || reportThemesReady));
  const showTrendSource =
    !isDenimPath &&
    showPalette &&
    hasSelectedPalette &&
    denimSpecsReady &&
    (hasPatternTrends || surfaceSlides.length > 0 || surfacesLoading);
  const showSurfaceSlides = showTrendSource && isSurfacePath;
  const showPattern =
    !isDenimPath && showTrendSource && isPatternPath && hasPatternTrends;
  const choseRecommend = patternChoice === "recommend";
  const choseManual = patternChoice === "choose";
  const showMicro = false;
  const showPatternStories = isWesternPatternFlow
    ? showPattern &&
      Boolean(activeMicroTheme) &&
      selectedMicroThemes.includes(activeMicroTheme)
    : showPattern && choseManual;
  const hasSelectedPatternStory = selectedPatternStoryIds.length > 0;
  const showPreferences = isWesternPatternFlow
    ? showPatternStories && Boolean(activePatternStoryId)
    : choseRecommend
      ? showPattern && Boolean(patternChoice)
      : showPatternStories && Boolean(activePatternStoryId);
  const surfaceReady = isSurfacePath && selectedSurfaceIds.length > 0;
  const patternSelectionReady = skipPatternSections
    ? showPalette && hasSelectedPalette && denimSpecsReady
    : surfaceReady
      ? showTrendSource
      : isWesternPatternFlow
        ? hasSelectedPatternStory
        : choseRecommend
          ? showPattern && Boolean(patternChoice)
          : hasSelectedPatternStory;
  const showLookDetails = isDenimPath
    ? hasRangePlanQuantity && hasSelectedPalette
    : hasRangePlanQuantity && patternSelectionReady;
  const showLookGarmentFormat = showLookDetails && !isDenimPath;
  // Denim skips garment/pattern format (BE wipe + IG parity) — moodboard after Look Details.
  // Print / Embroidery in Look Details is optional (BE Joi allows empty).
  const trendPathReadyForMoodboard = skipPatternSections
    ? showPalette && hasSelectedPalette && denimSpecsReady
    : surfaceReady
      ? showTrendSource
      : isWesternPatternFlow
        ? showPattern && hasSelectedPatternStory
        : choseRecommend
          ? showPattern && Boolean(patternChoice)
          : showPattern && choseManual && hasSelectedPatternStory;
  const showMoodboard =
    startingDirection === "Denim"
      ? hasSelectedPalette && denimSpecsReady && lookDetailsComplete
      : trendPathReadyForMoodboard && lookDetailsComplete;
  const catalogReadyForGenerate =
    skipsMacro || (reportThemesReady && Boolean(selectedMacro));
  const denimCatalogReady =
    startingDirection !== "Denim" ||
    (denimCatalog.materials.length > 0 && denimCatalog.washes.length > 0);
  const silhouetteUploadIncomplete =
    hasIncompleteSilhouetteUpload(draftLookGroups);
  const canGenerateCollection =
    showMoodboard &&
    catalogReadyForGenerate &&
    denimSpecsReady &&
    denimCatalogReady &&
    !silhouetteUploadIncomplete;
  const macroReportSlides = useMemo(
    () => reportSlidesForMacroThemeColour(selectedMacroReportPages),
    [selectedMacroReportPages]
  );
  const patternReportSlides = useMemo(
    () => reportSlidesForMacroThemePattern(selectedMacroReportPages),
    [selectedMacroReportPages]
  );
  const selectedMicroPatternReportSlides = useMemo(() => {
    const microId =
      activeMicroTheme && selectedMicroThemes.includes(activeMicroTheme)
        ? activeMicroTheme
        : selectedMicroThemes[0] || "";
    if (!isWesternPatternFlow || !microId) {
      return [];
    }
    const catalogMicro = catalogMicros.find(item => item.id === microId);
    // Western micro report pages often live on IG catalog microThemes; prefer
    // those so View Report is not empty when report-pages omits microThemes.
    const fromCatalogPages = reportSlidesFromPages(catalogMicro?.pages);
    if (fromCatalogPages.length >= 1) {
      return fromCatalogPages;
    }
    if (!selectedMacroReportPages) {
      return [];
    }
    return reportSlidesForMicroThemePattern(
      selectedMacroReportPages,
      microId,
      catalogMicro as Parameters<typeof reportSlidesForMicroThemePattern>[2]
    );
  }, [
    activeMicroTheme,
    catalogMicros,
    isWesternPatternFlow,
    selectedMacroReportPages,
    selectedMicroThemes,
  ]);
  // Ethnic: show whenever Pattern Trend section is up and a deck exists
  // (do not require choose/suggest — that hid the CTA). Western: selected micro.
  const patternTrendReportSlides = isWesternPatternFlow
    ? selectedMicroPatternReportSlides
    : showPattern
      ? patternReportSlides
      : [];
  const lookDetailsBreakdown = useMemo(
    () => rangePlanBreakdown(rangePlan),
    [rangePlan]
  );
  const handleDraftLookGroupsChange = useCallback(
    (groups: SilhouetteGroup[]) => {
      setDraftLookGroups(groups);
      // Sync session-level fallback from first filled style (do not clear hydrate).
      const fallback = garmentFormatFallbackFromGroups(groups);
      if (!fallback.garmentFormat) {
        return;
      }
      setGarmentFormat(fallback.garmentFormat);
      setPatternFormat(fallback.patternFormat);
      setEmbroideryType(fallback.embroideryType);
    },
    []
  );
  const catalogMessage =
    startingDirection === "Denim"
      ? denimCatalog.denimLoading &&
        !denimCatalog.materials.length &&
        !denimCatalog.washes.length
        ? "Loading denim catalog…"
        : !denimCatalog.palettes.length &&
            !denimCatalog.materials.length &&
            !denimCatalog.washes.length
          ? denimCatalog.denimError
            ? "Unable to load the denim catalog. Sign in and retry."
            : "No denim palettes, materials, or washes in the catalog."
          : catalogLoading
            ? "Loading catalog…"
            : undefined
      : startingDirection === "Macro-theme"
        ? catalogLoading
          ? "Loading catalog…"
          : catalogError
            ? "Unable to load catalog themes for these filters."
            : !hasReportThemes
              ? "No extracted reports for these filters."
              : undefined
        : startingDirection === "Capsules"
          ? catalogLoading
            ? "Loading catalog…"
            : catalogError
              ? "Unable to load catalog themes for these filters."
              : !hasCapsules
                ? "No capsule reports for these filters."
                : undefined
          : undefined;

  const resetFromDirectionOnward = useCallback(() => {
    setStartingDirection("");
    setMacroTheme("");
    setSelectedPaletteIds([]);
    setExtraPalettes([]);
    setDenimMaterialId("");
    setDenimWashId("");
    setTrendSource("");
    setSelectedSurfaceIds([]);
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
    setGarmentFormat("");
    setEmbroideryType("");
    setPatternFormat("");
    setAdditionalPrompt("");
    setConfirmSkipMoodboard(false);
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
  }, []);

  /** Empty the query form + ownership (sidebar new draft / leave ?cbSession=). */
  const resetQueryFormForNewDraft = useCallback(() => {
    hydratedFormSessionIdRef.current = null;
    pendingFormHydrationRef.current = null;
    pendingFormSessionIdRef.current = null;
    lastPersistedProgressKeyRef.current = "";
    lastPersistedPayloadJsonRef.current = "";
    adoptHydrateBaselineRef.current = false;
    clearCollectionBuildDraftSessionId();
    setIsHydratingSession(false);
    setActiveTab("query");
    setResultDetails(null);
    setLookGroups(undefined);
    setDraftLookGroups([]);
    setSeedLooks([]);
    setMarket("");
    setCategory("");
    setSeasonId("");
    setRangePlan([]);
    resetFromDirectionOnward();
    setPreferencePreviews({});
    setMoodboardSubmitting(false);
    setLooksSubmitting(false);
    setStayOnQuery(false);
    setEnqueueError(null);
  }, [resetFromDirectionOnward]);

  useEffect(() => {
    if (skipSilhouetteOpen) {
      return;
    }
    // While restoring a draft session, keep range plan until hydration finishes.
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam
    ) {
      return;
    }
    // One empty row so the product dropdown shows "Select product" (no default type).
    setRangePlan(inputsComplete ? [createRangePlanItem("")] : []);
    // sessionParam omitted on purpose: autosave binding ?cbSession= must not wipe range plan.
  }, [inputsComplete, market, category, skipSilhouetteOpen]);

  // Migrate/strip IG-shared `?session=` so CB never treats an IG output id as a CB session.
  useEffect(() => {
    const hasLegacy = searchParams.has(COLLECTION_BUILD_SESSION_PARAM_LEGACY);
    if (!hasLegacy) {
      return;
    }
    const legacyId = legacySessionParam;
    const cbId = sessionParam;
    let cancelled = false;
    const migrate = async () => {
      let nextCbId = cbId;
      if (!nextCbId && legacyId) {
        try {
          const session = await getCollectionBuildSession(legacyId);
          if (!cancelled && session?.sessionId) {
            nextCbId = session.sessionId;
          }
        } catch {
          // Not a collection_build session (often an IG `?session=` leftover).
        }
      }
      if (legacyId && userId) {
        clearMistakenImageGeneratorPendingId(userId, legacyId);
      }
      if (cancelled) {
        return;
      }
      if (nextCbId) {
        setSearchParams(collectionBuildSessionSearchParams(nextCbId), {
          replace: true,
        });
        return;
      }
      setSearchParams({}, { replace: true });
    };
    void migrate();
    return () => {
      cancelled = true;
    };
  }, [legacySessionParam, sessionParam, setSearchParams, userId]);

  useEffect(() => {
    const newDraftRequested =
      newDraftNonce !== lastHandledNewDraftNonceRef.current;
    if (newDraftRequested) {
      lastHandledNewDraftNonceRef.current = newDraftNonce;
      prevSessionParamRef.current = undefined;
      resetQueryFormForNewDraft();
      // startNewDraft navigates without query; strip immediately if still bound.
      if (sessionParam) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (!sessionParam) {
      const leftSession = prevSessionParamRef.current != null;
      prevSessionParamRef.current = undefined;
      // Only empty on leave-session. Stay idle on blank CB so we do not bump
      // draftEpoch / wipe an in-flight create (that caused 2–3 POSTs on fill).
      if (leftSession) {
        resetQueryFormForNewDraft();
      } else {
        setIsHydratingSession(false);
      }
      return;
    }
    // Autosave / generate may add ?cbSession= for a draft we already own — do not
    // treat that as a Recents open (no loader, no form wipe/re-fill).
    // Only trust hydratedFormSessionIdRef (set synchronously before setSearchParams);
    // peek alone is unsafe after SPA soft-nav / Recents reopen.
    const alreadyOwned = hydratedFormSessionIdRef.current === sessionParam;
    rememberCollectionBuildDraftSessionId(sessionParam);
    const switched =
      prevSessionParamRef.current != null &&
      prevSessionParamRef.current !== sessionParam;
    // Gate UI only when opening/switching sessions — never for owned-draft URL bind.
    const needsHydrateGate = switched || !alreadyOwned;
    prevSessionParamRef.current = sessionParam;
    // Switching Recents sessions drops the prior frozen snapshot; first assign keeps generate snapshot.
    if (switched) {
      hydratedFormSessionIdRef.current = null;
      pendingFormHydrationRef.current = null;
      pendingFormSessionIdRef.current = null;
      lastPersistedProgressKeyRef.current = "";
      lastPersistedPayloadJsonRef.current = "";
      adoptHydrateBaselineRef.current = false;
      setStayOnQuery(false);
      setEnqueueError(null);
      setResultDetails(null);
      setLookGroups(undefined);
      setMarket("");
      setCategory("");
      setSeasonId("");
      setRangePlan([]);
      resetFromDirectionOnward();
    } else if (alreadyOwned) {
      // URL gained cbSession after create — keep editing; mark hydrate complete.
      // Do not GET/re-hydrate: that would race local form state ahead of the last PATCH.
      setIsHydratingSession(false);
      return;
    }
    if (needsHydrateGate) {
      setIsHydratingSession(true);
    }
    let cancelled = false;
    void loadSession(sessionParam).then(session => {
      if (cancelled) {
        return;
      }
      if (!session) {
        setIsHydratingSession(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    loadSession,
    newDraftNonce,
    resetFromDirectionOnward,
    resetQueryFormForNewDraft,
    sessionParam,
    setSearchParams,
  ]);

  useEffect(() => {
    if (
      !activeSession ||
      !sessionParam ||
      activeSession.sessionId !== sessionParam
    ) {
      return;
    }
    // Generated sessions open Results with the query form still visible (locked).
    // stayOnQuery keeps the wizard editable after Back to query on a failed/generated session.
    if (collectionSessionHasOutputs(activeSession) && !stayOnQuery) {
      adoptHydrateBaselineRef.current = false;
      setIsHydratingSession(false);
      const details = detailsFromCollectionSession(activeSession);
      if (details) {
        // Keep the generate-time snapshot (frozen Design Details); only fill when empty (reopen).
        // Always enrich ids → labels / preference asset urls when catalog is available.
        // Recommend path: merge BE/catalog stems from poll into the live snapshot.
        setResultDetails(prev =>
          enrichCollectionBuildDetails(
            prev
              ? mergeCollectionBuildDetailsPreferences(
                  prev,
                  details.preferences
                )
              : details,
            catalog,
            denimCatalogForDetails
          )
        );
      }
      // Fresh generate already owns form state; Recents reopen must restore it above results.
      if (hydratedFormSessionIdRef.current !== sessionParam) {
        const hydration = formHydrationFromCollectionSession(activeSession);
        if (hydration) {
          hydratedFormSessionIdRef.current = sessionParam;
          pendingFormHydrationRef.current = hydration;
          pendingFormSessionIdRef.current = sessionParam;
          setMarket(hydration.market);
          setCategory(hydration.category);
          if (hydration.seasonId) {
            setSeasonId(hydration.seasonId);
          } else {
            setSeasonId("");
          }
        } else {
          pendingFormHydrationRef.current = null;
          pendingFormSessionIdRef.current = null;
        }
      } else {
        pendingFormHydrationRef.current = null;
        pendingFormSessionIdRef.current = null;
      }
      return;
    }
    if (hydratedFormSessionIdRef.current === sessionParam) {
      return;
    }
    const hydration = formHydrationFromCollectionSession(activeSession);
    if (!hydration) {
      // Empty draft — show blank form; a later fetch may still populate.
      setIsHydratingSession(false);
      return;
    }
    hydratedFormSessionIdRef.current = sessionParam;
    pendingFormHydrationRef.current = hydration;
    pendingFormSessionIdRef.current = sessionParam;
    // Baseline is adopted only after finish-hydration restores a saved range plan.
    adoptHydrateBaselineRef.current = false;
    lastPersistedProgressKeyRef.current = "";
    lastPersistedPayloadJsonRef.current = "";
    setIsHydratingSession(true);
    setActiveTab("query");
    setResultDetails(null);
    setLookGroups(undefined);
    setMarket(hydration.market);
    setCategory(hydration.category);
    if (hydration.seasonId) {
      setSeasonId(hydration.seasonId);
    } else {
      // Drop leftover season from a prior draft so finish-hydration can resolve by label.
      setSeasonId("");
    }
  }, [
    activeSession,
    catalog,
    denimCatalogForDetails,
    sessionParam,
    stayOnQuery,
  ]);

  // When catalog arrives after results open, resolve theme labels + preference urls.
  useEffect(() => {
    if (
      !catalog &&
      !denimCatalog.washes.length &&
      !denimCatalog.materials.length
    ) {
      return;
    }
    setResultDetails(prev =>
      prev
        ? enrichCollectionBuildDetails(prev, catalog, denimCatalogForDetails)
        : prev
    );
  }, [catalog, denimCatalogForDetails]);

  // Finish draft hydration after market/category unlock seasons + inputsComplete.
  useEffect(() => {
    const pending = pendingFormHydrationRef.current;
    const pendingId = pendingFormSessionIdRef.current;
    if (!pending || pendingId == null || pendingId !== sessionParam) {
      return;
    }
    if (!market || !category) {
      return;
    }
    // Wait for React to apply hydrated market/category (ignore leftover prior draft).
    if (market !== pending.market || category !== pending.category) {
      return;
    }

    let nextSeasonId = seasonId;
    if (pending.seasonId && seasonId && seasonId !== pending.seasonId) {
      // Leftover seasonId from a prior draft — wait for hydration setState or catalog remap.
      if (seasonsLoading) {
        return;
      }
      if (seasons.some(item => item.id === pending.seasonId)) {
        setSeasonId(pending.seasonId);
        return;
      }
      // Stored id not in catalog; resolve by label below.
      nextSeasonId = "";
    }
    if (!nextSeasonId) {
      // Wait until the season catalog is available (empty list can mean not ready yet).
      if (seasonsLoading) {
        return;
      }
      if (!seasons.length) {
        // Seasons fetch finished empty — unlock UI; keep pending for cascade gating.
        setIsHydratingSession(false);
        return;
      }
      const match =
        (pending.seasonId &&
          seasons.find(item => item.id === pending.seasonId)) ||
        (pending.seasonLabel &&
          seasons.find(item => item.label === pending.seasonLabel)) ||
        undefined;
      if (!match) {
        // Catalog loaded but no match — reveal form so the user can pick a season;
        // keep pending until inputsComplete so range/direction still apply later.
        setIsHydratingSession(false);
        return;
      }
      nextSeasonId = match.id;
      setSeasonId(match.id);
      return;
    }

    if (!inputsComplete) {
      return;
    }

    setRangePlan(
      pending.rangePlan.length
        ? pending.rangePlan.map(item =>
            createRangePlanItem(item.productType, item.quantity)
          )
        : [createRangePlanItem("")]
    );
    setStartingDirection(pending.startingDirection);
    setMacroTheme(pending.macroTheme);
    setSelectedPaletteIds(pending.selectedPaletteIds);
    setExtraPalettes(pending.extraPalettes);
    setDenimMaterialId(pending.denimMaterialId);
    setDenimWashId(pending.denimWashId);
    setSelectedSurfaceIds(pending.surfaceIds);
    setTrendSource(
      pending.surfaceIds.length
        ? "surface"
        : pending.patternChoice || pending.microThemes.length
          ? "pattern"
          : ""
    );
    setPatternChoice(pending.patternChoice);
    // pending.microThemes holds persisted patternTrendIds (stories and/or legacy micros).
    setSelectedMicroThemes(pending.microThemes);
    setActiveMicroTheme(pending.microThemes[0] || "");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    remapPatternSelectionRef.current = true;
    setSelectedPreferenceIds(pending.selectedPreferenceIds);
    setGarmentFormat(pending.garmentFormat);
    setEmbroideryType(pending.embroideryType);
    setPatternFormat(pending.patternFormat);
    setAdditionalPrompt(pending.additionalPrompt);
    setSeedLooks(pending.looks || []);
    pendingFormHydrationRef.current = null;
    // Only skip the post-hydrate autosave when we restored real progress the user already saved.
    adoptHydrateBaselineRef.current = pending.rangePlan.some(
      item => item.quantity > 0
    );
    // Reveal fully populated form in the same paint as restored fields.
    setIsHydratingSession(false);
    // Unlock cascade resets after sibling effects for this commit have run.
    window.setTimeout(() => {
      if (pendingFormSessionIdRef.current === pendingId) {
        pendingFormSessionIdRef.current = null;
      }
    }, 0);
  }, [
    category,
    inputsComplete,
    market,
    seasonId,
    seasons,
    seasonsLoading,
    sessionParam,
  ]);

  useEffect(() => {
    if (!remapPatternSelectionRef.current || !selectedMacro) {
      return;
    }
    remapPatternSelectionRef.current = false;
    const resolved = hydrateMicroAndStorySelection(
      selectedMacro,
      selectedMicroThemes
    );
    const storyIds = resolved.storyIds;
    if (isWesternPatternFlow) {
      if (resolved.microIds.length) {
        setSelectedMicroThemes(resolved.microIds);
        setActiveMicroTheme(resolved.activeMicroId);
      }
      setSelectedPatternStoryIds(storyIds);
      const activeUnderMicro =
        storyIds.find(id =>
          catalogPatternStoriesForMicro(
            selectedMacro,
            resolved.activeMicroId
          ).some(trend => String(trend.id) === id)
        ) ||
        storyIds[0] ||
        "";
      setActivePatternStoryId(activeUnderMicro);
      return;
    }
    if (storyIds.length) {
      setSelectedPatternStoryIds(storyIds);
      setActivePatternStoryId(storyIds[0] || "");
    }
  }, [isWesternPatternFlow, selectedMacro, selectedMicroThemes]);

  // Persist session after meaningful wizard blocks (not every keystroke/toggle).
  // Use seasonLabel (not seasonId) so catalog id remaps do not look like user edits.
  const looksProgressKey = useMemo(
    () =>
      draftLookGroups
        .flatMap(group =>
          group.styles.map(
            (style, index) =>
              `${group.productType}:${index}:${style.influence}:${style.styleType}:${style.materials}:${style.denimMaterialId || ""}:${style.sleeve}:${style.neckline}:${style.fit}:${style.wash}:${style.denimWashId || ""}:${style.length}:${(
                style.garmentFormats || []
              )
                .map(
                  entry =>
                    `${entry.format}:${entry.patternFormat}:${entry.embroideryType || ""}`
                )
                .join(
                  "/"
                )}:${style.garmentFormat}:${style.patternFormat}:${style.embroideryType}:${style.selectedSketchIds.join(",")}:${style.uploadFileName}:${style.uploadUrl || ""}`
          )
        )
        .join(";"),
    [draftLookGroups]
  );
  const sessionProgressKey = useMemo(() => {
    if (!inputsComplete || !hasRangePlanQuantity || !seasonLabel) {
      return "";
    }
    return [
      market,
      category,
      seasonLabel,
      rangePlan
        .filter(item => item.quantity > 0)
        .map(item => `${item.productType}:${item.quantity}`)
        .join(","),
      startingDirection,
      macroTheme,
      selectedPaletteIds.join(","),
      denimMaterialId,
      denimWashId,
      trendSource,
      selectedSurfaceIds.join(","),
      patternChoice,
      selectedMicroThemes.join(","),
      activeMicroTheme,
      selectedPatternStoryIds.join(","),
      activePatternStoryId,
      selectedPreferenceIds.join(","),
      garmentFormat,
      embroideryType,
      patternFormat,
      recentsThemeLabel,
      looksProgressKey,
    ].join("|");
  }, [
    activeMicroTheme,
    activePatternStoryId,
    category,
    denimMaterialId,
    denimWashId,
    embroideryType,
    garmentFormat,
    hasRangePlanQuantity,
    inputsComplete,
    looksProgressKey,
    macroTheme,
    market,
    patternChoice,
    patternFormat,
    rangePlan,
    seasonLabel,
    selectedMicroThemes,
    selectedPaletteIds,
    selectedPatternStoryIds,
    selectedPreferenceIds,
    selectedSurfaceIds,
    startingDirection,
    trendSource,
    recentsThemeLabel,
  ]);

  useEffect(() => {
    if (isDenimPath || !market || !category || !seasonLabel) {
      setSurfaceSlides([]);
      setSurfacesLoading(false);
      return;
    }
    let cancelled = false;
    setSurfacesLoading(true);
    getImageGeneratorSurfacesCatalog({
      season: seasonLabel,
      seasonId: seasonId || undefined,
      market,
      category,
      genre: LOCKED_GENRE,
    })
      .then(next => {
        if (cancelled) {
          return;
        }
        const slides = next?.surfaces || [];
        setSurfaceSlides(slides);
        setSelectedSurfaceIds(prev =>
          prev.filter(id => slides.some(slide => slide.id === id))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setSurfaceSlides([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSurfacesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // selectedSurfaceIds intentionally omitted — keep/clear handled inside.
  }, [isDenimPath, market, category, seasonLabel, seasonId]);

  useEffect(() => {
    if (isDenimPath || !market || !category || !seasonLabel) {
      setReportPagesCatalog(null);
      return;
    }
    let cancelled = false;
    getCollectionBuildReportPagesCatalog({
      season: seasonLabel,
      seasonId: seasonId || undefined,
      market,
      category,
    })
      .then(next => {
        if (!cancelled) {
          setReportPagesCatalog(next || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReportPagesCatalog(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isDenimPath, market, category, seasonLabel, seasonId]);

  useEffect(() => {
    if (
      !sessionProgressKey ||
      resultDetails ||
      moodboardSubmitting ||
      looksSubmitting ||
      skipSilhouetteOpen ||
      activeTab !== "query" ||
      isHydratingSession ||
      pendingFormSessionIdRef.current != null
    ) {
      return;
    }
    // Hydrate / open settle: adopt current key as baseline — never PATCH on load.
    if (adoptHydrateBaselineRef.current) {
      lastPersistedProgressKeyRef.current = sessionProgressKey;
      // Stay in adopt mode until report catalog settles and pattern skip has cleared choices.
      if (catalogNeeded && catalogLoading) {
        return;
      }
      if (skipPatternSections && patternChoice) {
        return;
      }
      adoptHydrateBaselineRef.current = false;
      try {
        lastPersistedPayloadJsonRef.current = JSON.stringify(
          buildPersistPayload(false)
        );
      } catch {
        lastPersistedPayloadJsonRef.current = "";
      }
      return;
    }
    if (sessionProgressKey === lastPersistedProgressKeyRef.current) {
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const persist = buildPersistPayload(false);
          if (!persist.rangePlan?.items?.length) {
            return;
          }
          const payloadJson = JSON.stringify(persist);
          if (payloadJson === lastPersistedPayloadJsonRef.current) {
            lastPersistedProgressKeyRef.current = sessionProgressKey;
            return;
          }
          // Prefer URL, then hydrated bind, then module draft — never POST over an open session.
          const boundId =
            sessionParam ||
            hydratedFormSessionIdRef.current ||
            peekCollectionBuildDraftSessionId();
          const sessionId = await persistCollectionBuildSession(
            persist,
            boundId
          );
          if (cancelled || !sessionId) {
            return;
          }
          lastPersistedProgressKeyRef.current = sessionProgressKey;
          lastPersistedPayloadJsonRef.current = payloadJson;
          refreshSummaryFromForm(sessionId);
          // Sync Recents from list API without waiting for accordion/tab click.
          void refreshSummaries();
          // Own the draft before URL rewrite so the sessionParam effect skips hydrate loader.
          // Do not watchSession here — that GETs + polls and can re-hydrate/wipe mid-fill.
          hydratedFormSessionIdRef.current = sessionId;
          rememberCollectionBuildDraftSessionId(sessionId);
          if (sessionParam !== sessionId) {
            setSearchParams(collectionBuildSessionSearchParams(sessionId), {
              replace: true,
            });
          }
        } catch (error) {
          // Progressive update is best-effort; first create failure must be visible.
          if (!sessionParam && !hydratedFormSessionIdRef.current) {
            toast.error(
              getCollectionBuildErrorMessage(
                error,
                "Unable to save collection session."
              )
            );
          }
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    sessionProgressKey,
    resultDetails,
    moodboardSubmitting,
    looksSubmitting,
    skipSilhouetteOpen,
    activeTab,
    isHydratingSession,
    sessionParam,
    setSearchParams,
    buildPersistPayload,
    refreshSummaryFromForm,
    catalogNeeded,
    catalogLoading,
    skipPatternSections,
    patternChoice,
    refreshSummaries,
  ]);

  useEffect(() => {
    if (skipSilhouetteOpen) {
      return;
    }
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam
    ) {
      return;
    }
    resetFromDirectionOnward();
  }, [
    market,
    category,
    seasonId,
    skipSilhouetteOpen,
    resetFromDirectionOnward,
  ]);

  useEffect(() => {
    if (hasRangePlanQuantity) {
      return;
    }
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam
    ) {
      return;
    }
    resetFromDirectionOnward();
  }, [hasRangePlanQuantity, resetFromDirectionOnward]);

  useEffect(() => {
    if (skipSilhouetteOpen || !directionsSettled || !startingDirection) {
      return;
    }
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam
    ) {
      return;
    }
    // While report catalog is still loading, Macro/Capsules are temporarily absent —
    // do not wipe a hydrated or user-chosen direction.
    if (catalogNeeded && catalogLoading) {
      return;
    }
    // Ethnic + Denim is handled below (prefer Macro-theme over a full wipe).
    if (startingDirection === "Denim" && !denimAllowed) {
      return;
    }
    if (!availableDirections.includes(startingDirection)) {
      resetFromDirectionOnward();
    }
  }, [
    availableDirections,
    catalogLoading,
    catalogNeeded,
    denimAllowed,
    directionsSettled,
    resetFromDirectionOnward,
    skipSilhouetteOpen,
    startingDirection,
  ]);

  // Ethnic RTW: Denim is unavailable — clear denim fields and switch to Macro-theme when possible.
  useEffect(() => {
    if (denimAllowed || startingDirection !== "Denim") {
      return;
    }
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam
    ) {
      return;
    }
    setDenimMaterialId("");
    setDenimWashId("");
    setSelectedPaletteIds([]);
    setExtraPalettes([]);
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
    setGarmentFormat("");
    setEmbroideryType("");
    setPatternFormat("");
    setConfirmSkipMoodboard(false);
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
    if (availableDirections.includes("Macro-theme")) {
      setStartingDirection("Macro-theme");
      setMacroTheme("");
    } else if (availableDirections.includes("Capsules")) {
      setStartingDirection("Capsules");
      setMacroTheme("");
    } else {
      setStartingDirection("");
      setMacroTheme("");
    }
  }, [availableDirections, denimAllowed, sessionParam, startingDirection]);

  useEffect(() => {
    if (!skipSilhouetteOpen) {
      return;
    }
    // Freeze Look Details in view when skipping moodboard for looks.
    const frame = window.requestAnimationFrame(() => {
      scrollCollectionSectionIntoView(lookDetailsRef.current, "start");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [skipSilhouetteOpen]);

  useEffect(() => {
    if (!seasonEnabled) {
      if (seasonId) {
        setSeasonId("");
      }
      return;
    }
    // Empty seasons often means "not ready yet", not "invalid season".
    if (!seasonId || seasonsLoading || !seasons.length) {
      return;
    }
    if (seasons.some(item => item.id === seasonId)) {
      return;
    }
    // During draft hydrate, map stored season label → catalog id instead of clearing.
    if (
      pendingFormSessionIdRef.current != null &&
      pendingFormSessionIdRef.current === sessionParam &&
      pendingFormHydrationRef.current
    ) {
      const pending = pendingFormHydrationRef.current;
      const match =
        seasons.find(item => item.id === pending.seasonId) ||
        seasons.find(item => item.label === pending.seasonLabel);
      if (match) {
        setSeasonId(match.id);
        return;
      }
      if (!seasons.length) {
        return;
      }
    }
    setSeasonId("");
  }, [seasonEnabled, seasonId, seasons, seasonsLoading]);

  useEffect(() => {
    if (!skipPatternSections) {
      return;
    }
    setPatternChoice("");
    setSelectedPreferenceIds([]);
    if (startingDirection !== "Denim") {
      return;
    }
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setGarmentFormat("");
    setEmbroideryType("");
    setPatternFormat("");
  }, [skipPatternSections, startingDirection]);

  useEffect(() => {
    const assets = activeTrendAssets.filter(asset => asset.available);
    let cancelled = false;
    (async () => {
      const missing = assets.filter(
        asset => !assetPreviewCache.current.has(asset.stem)
      );
      if (!missing.length) {
        setPreferencePreviews(Object.fromEntries(assetPreviewCache.current));
        return;
      }
      await Promise.all(
        missing.map(async asset => {
          try {
            const preview = await getGenerativeAssetPreview(asset.url);
            assetPreviewCache.current.set(asset.stem, preview);
          } catch {
            // Leave uncached so the next pass can retry.
          }
        })
      );
      if (!cancelled) {
        setPreferencePreviews(Object.fromEntries(assetPreviewCache.current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTrendAssets]);

  const updateRangePlan = (id: string, patch: Partial<RangePlanItem>) => {
    setRangePlan(prev => {
      const currentSum = sumRangePlanQuantity(prev);
      return prev.map(item => {
        if (item.id !== id) {
          return item;
        }
        const next = { ...item, ...patch };
        if (patch.quantity !== undefined) {
          const remaining = MAX_RANGE_PRODUCTS - (currentSum - item.quantity);
          next.quantity = Math.max(
            0,
            Math.min(MAX_QUANTITY, remaining, patch.quantity)
          );
        }
        return next;
      });
    });
  };

  const handleAddProduct = () => {
    if (!canAddProduct || !productTypeOptions.length) {
      return;
    }
    setRangePlan(prev => [...prev, createRangePlanItem("")]);
  };

  const handleRemoveProduct = (id: string) => {
    setRangePlan(prev => prev.filter(item => item.id !== id));
  };

  const resetFromDenimPaletteOnward = () => {
    // Material/wash live on Look Details now — only clear session fallbacks.
    setDenimMaterialId("");
    setDenimWashId("");
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
    setGarmentFormat("");
    setEmbroideryType("");
    setPatternFormat("");
    setConfirmSkipMoodboard(false);
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
  };

  const handleStartingDirection = (value: StartingDirection | "") => {
    setStartingDirection(value);
    setMacroTheme("");
    setSelectedPaletteIds([]);
    setExtraPalettes([]);
    setDenimMaterialId("");
    setDenimWashId("");
    setTrendSource("");
    setSelectedSurfaceIds([]);
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
    // Print/Embroidery live in Look Details (after pattern selection). Clear only for Denim.
    if (value === "Denim") {
      setGarmentFormat("");
      setEmbroideryType("");
      setPatternFormat("");
    }
  };

  const handleMacroTheme = (value: string) => {
    setMacroTheme(value);
    setSelectedPaletteIds([]);
    setExtraPalettes([]);
    setTrendSource("");
    setSelectedSurfaceIds([]);
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
  };

  const handleTogglePalette = (id: string) => {
    setSelectedPaletteIds(prev => {
      const next = prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id];
      if (next.length === 0) {
        if (startingDirection === "Denim") {
          resetFromDenimPaletteOnward();
        } else {
          setTrendSource("");
          setSelectedSurfaceIds([]);
          setPatternChoice("");
          setSelectedMicroThemes([]);
          setActiveMicroTheme("");
          setSelectedPatternStoryIds([]);
          setActivePatternStoryId("");
          setSelectedPreferenceIds([]);
        }
      }
      return next;
    });
  };

  const handleAddPalette = (palette: ColorPaletteOption) => {
    setExtraPalettes(prev => [...prev, palette]);
    setSelectedPaletteIds(prev =>
      prev.includes(palette.id) ? prev : [...prev, palette.id]
    );
  };

  const handlePatternChoice = (value: PatternTrendChoice | "") => {
    setPatternChoice(value);
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
  };

  const handleTrendSource = (value: TrendSourceChoice | "") => {
    setTrendSource(value);
    setSelectedSurfaceIds([]);
    setPatternChoice("");
    setSelectedMicroThemes([]);
    setActiveMicroTheme("");
    setSelectedPatternStoryIds([]);
    setActivePatternStoryId("");
    setSelectedPreferenceIds([]);
  };

  const handleToggleSurfaceId = (id: string) => {
    const trimmed = String(id || "").trim();
    if (!trimmed) {
      return;
    }
    setSelectedSurfaceIds(prev =>
      prev.includes(trimmed)
        ? prev.filter(item => item !== trimmed)
        : [...prev, trimmed]
    );
  };

  const handleSelectMicroTheme = (value: string) => {
    const alreadySelected = selectedMicroThemes.includes(value);
    const isActive = activeMicroTheme === value;
    const stemsForMicro = (microId: string) =>
      catalogPatternStoriesForMicro(selectedMacro, microId).flatMap(trend =>
        (trend.assets || []).map(asset => asset.stem)
      );
    const storyIdsForMicro = (microId: string) =>
      new Set(
        catalogPatternStoriesForMicro(selectedMacro, microId).map(trend =>
          String(trend.id)
        )
      );
    const syncActiveStoryForMicro = (
      microId: string,
      storyIds: string[]
    ): string => {
      if (!microId) {
        return "";
      }
      const underMicro = storyIdsForMicro(microId);
      if (activePatternStoryId && underMicro.has(activePatternStoryId)) {
        return activePatternStoryId;
      }
      return storyIds.find(id => underMicro.has(id)) || "";
    };

    if (alreadySelected) {
      const nextSelected = selectedMicroThemes.filter(id => id !== value);
      const removedStories = storyIdsForMicro(value);
      const nextStories = selectedPatternStoryIds.filter(
        id => !removedStories.has(id)
      );
      const stemsStillOwned = new Set(
        nextSelected.flatMap(id => stemsForMicro(id))
      );
      const stemsFromRemoved = new Set(stemsForMicro(value));
      setSelectedMicroThemes(nextSelected);
      setSelectedPatternStoryIds(nextStories);
      setSelectedPreferenceIds(prev =>
        prev.filter(
          stem => !stemsFromRemoved.has(stem) || stemsStillOwned.has(stem)
        )
      );
      const nextActive = isActive ? nextSelected[0] || "" : activeMicroTheme;
      setActiveMicroTheme(nextActive);
      setActivePatternStoryId(syncActiveStoryForMicro(nextActive, nextStories));
      return;
    }

    setSelectedMicroThemes(prev =>
      prev.includes(value) ? prev : [...prev, value]
    );
    setActiveMicroTheme(value);
    setActivePatternStoryId(
      syncActiveStoryForMicro(value, selectedPatternStoryIds)
    );
  };

  const handleSelectPatternStory = (value: string) => {
    const alreadySelected = selectedPatternStoryIds.includes(value);
    const isActive = activePatternStoryId === value;
    const stemsForStory = (storyId: string) => {
      const pool = isWesternPatternFlow
        ? selectedMicroThemes.flatMap(microId =>
            catalogPatternStoriesForMicro(selectedMacro, microId)
          )
        : patternTrends;
      const story = pool.find(item => String(item.id) === String(storyId));
      return new Set((story?.assets || []).map(asset => asset.stem));
    };

    if (alreadySelected) {
      const nextStories = selectedPatternStoryIds.filter(id => id !== value);
      const removedStems = stemsForStory(value);
      const stemsStillOwned = new Set(
        nextStories.flatMap(id => Array.from(stemsForStory(id)))
      );
      setSelectedPatternStoryIds(nextStories);
      setActivePatternStoryId(
        isActive ? nextStories[0] || "" : activePatternStoryId
      );
      setSelectedPreferenceIds(prev =>
        prev.filter(
          stem => !removedStems.has(stem) || stemsStillOwned.has(stem)
        )
      );
      return;
    }

    setSelectedPatternStoryIds(prev =>
      prev.includes(value) ? prev : [...prev, value]
    );
    setActivePatternStoryId(value);
  };

  const handleTogglePreference = (id: string) => {
    // Preferences are optional — clearing them must not collapse later steps.
    setSelectedPreferenceIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const buildResultDetails = (
    skipMoodboard = false
  ): CollectionBuildResultDetails => {
    const denim = startingDirection === "Denim";
    const fromStyles = denimSessionIdsFromGroups(draftLookGroups);
    const macroLabel =
      macroOptions.find(item => item.value === macroTheme)?.label ||
      humanizeCollectionThemeId(macroTheme);
    const denimStyleTiles = denim
      ? denimTilesFromStyleSelections(
          draftLookGroups.flatMap(group => group.styles),
          denimCatalog.materials,
          denimCatalog.washes
        )
      : null;
    const washLabel = denim
      ? resolveDenimWashDisplay(
          denimStyleTiles?.denimWashTile?.id ||
            fromStyles.denimWashId ||
            denimWashId,
          denimCatalog.washes
        )
      : "";
    const preferenceStems = denim ? [] : selectedPreferenceIds;
    const microThemes = denim
      ? []
      : selectedMicroThemes
          .map(
            id =>
              microOptions.find(item => item.value === id)?.label ||
              catalogMicroThemeLabel(catalog, id) ||
              id
          )
          .filter(Boolean);
    return snapshotCollectionBuildDetails({
      genre: LOCKED_GENRE,
      season: seasonLabel,
      market,
      // Keep API value (Ethnic RTW / Western RTW); UI strips via categoryLabel in rows.
      category,
      rangePlanTotal: sumRangePlanQuantity(rangePlan),
      rangePlanBreakdown: rangePlanBreakdown(rangePlan),
      startingDirection,
      macroTheme: denim ? "" : macroLabel,
      patternTrend: denim ? "" : patternTrendLabel(patternChoice),
      microTheme: microThemes.join(", "),
      microThemes,
      wash: washLabel,
      denimMaterialTile: denimStyleTiles?.denimMaterialTile,
      denimWashTile: denimStyleTiles?.denimWashTile,
      denimMaterialTiles: denimStyleTiles?.denimMaterialTiles,
      denimWashTiles: denimStyleTiles?.denimWashTiles,
      garmentFormat: denim
        ? ""
        : garmentFormatFallbackFromGroups(draftLookGroups).garmentFormat ||
          garmentFormat,
      patternFormat: denim
        ? ""
        : garmentFormatFallbackFromGroups(draftLookGroups).patternFormat ||
          patternFormat,
      palettes: selectedPalettes,
      paletteHexes: selectedPalettes.flatMap(palette => palette.colors),
      // Prefer catalog asset urls (auth-gated); fall back to form blob previews for
      // live generate when catalog stems are not yet resolvable.
      preferences: preferenceOptionsForDetails(preferenceStems, catalog, [
        ...selectedTrendAssets,
        ...activeTrendAssets,
        ...flattenCatalogSketchAssets(catalog),
      ]).map(item => ({
        ...item,
        imageUrl: item.imageUrl || preferencePreviews[item.id] || "",
      })),
      surfaceTrends: denim
        ? undefined
        : selectedSurfaceIds
            .map(id => ({
              id,
              name:
                surfaceSlides.find(slide => slide.id === id)?.name?.trim() ||
                "",
            }))
            .filter(item => item.name),
      sketchAssets,
      skipMoodboard,
      persistPayload: buildPersistPayload(skipMoodboard),
    });
  };

  const enterLookBook = (
    details: CollectionBuildResultDetails,
    groups: SilhouetteGroup[]
  ) => {
    setLookGroups(groups);
    setResultDetails(details);
    setConfirmSkipMoodboard(false);
  };

  const handleGenerateMoodboard = async () => {
    if (!canGenerateCollection || moodboardSubmitting) {
      return;
    }
    if (hasIncompleteSilhouetteUpload(draftLookGroups)) {
      toast.error(INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE);
      scrollCollectionSectionIntoView(lookDetailsRef.current, "start");
      return;
    }
    const details = buildResultDetails(false);
    const persist = buildPersistPayload(false);
    setMoodboardSubmitting(true);
    setStayOnQuery(false);
    setEnqueueError(null);
    // Freeze Design Details + look groups before session URL/poll updates.
    // Looks were already chosen above moodboard — Proceed to Looks uses these.
    setLookGroups(draftLookGroups);
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
    setResultDetails(details);
    setConfirmSkipMoodboard(false);
    try {
      const sessionId = await ensureCollectionBuildSessionId(
        persist,
        sessionParam ||
          hydratedFormSessionIdRef.current ||
          peekCollectionBuildDraftSessionId()
      );
      hydratedFormSessionIdRef.current = sessionId;
      rememberCollectionBuildDraftSessionId(sessionId);
      refreshSummaryFromForm(sessionId, "generating");
      void refreshSummaries();
      setSearchParams(collectionBuildSessionSearchParams(sessionId), {
        replace: true,
      });
      // Watch only after enqueue — a pre-generate GET sees no outputs, drops
      // pending, and stops polling before the moodboard row exists.
      const record = await generateCollectionBuildMoodboard({
        ...persist,
        sessionId,
      });
      const assembledPrefs = preferencesFromCollectionRecords([record]);
      if (assembledPrefs.length) {
        setResultDetails(prev =>
          prev
            ? enrichCollectionBuildDetails(
                mergeCollectionBuildDetailsPreferences(prev, assembledPrefs),
                catalog,
                denimCatalogForDetails
              )
            : prev
        );
      }
      const nextSessionId = record.sessionId || sessionId;
      hydratedFormSessionIdRef.current = nextSessionId;
      rememberCollectionBuildDraftSessionId(nextSessionId);
      refreshSummaryFromForm(nextSessionId, "generating");
      void refreshSummaries();
      watchSession(nextSessionId);
      if (nextSessionId !== sessionId) {
        setSearchParams(collectionBuildSessionSearchParams(nextSessionId), {
          replace: true,
        });
      }
    } catch (error) {
      const message = getCollectionBuildErrorMessage(
        error,
        error instanceof Error
          ? error.message
          : "Unable to start moodboard generation."
      );
      setEnqueueError(message);
      toast.error(
        collectionBuildGenerationErrorCopy("moodboard", message).toast
      );
    } finally {
      setMoodboardSubmitting(false);
    }
  };

  const handleSkipGenerateLook = async (groups: SilhouetteGroup[]) => {
    if (
      looksSubmitting ||
      !areSilhouetteGroupsComplete(groups, {
        category,
        market,
        startingDirection,
      })
    ) {
      return;
    }
    if (hasIncompleteSilhouetteUpload(groups)) {
      toast.error(INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE);
      scrollCollectionSectionIntoView(lookDetailsRef.current, "start");
      return;
    }
    const looks = looksPayloadFromGroups(groups);
    if (!looks.length) {
      toast.error("Add at least one complete look before generating.");
      return;
    }
    const details = pendingSkipDetails || buildResultDetails(true);
    const persist =
      (details.persistPayload as CollectionBuildSessionPayload | undefined) ||
      buildPersistPayload(true);
    setLooksSubmitting(true);
    setStayOnQuery(false);
    setEnqueueError(null);
    // Leave query locked on Results, do not reopen Look Details.
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
    // Freeze Design Details + silhouette groups before session URL/poll updates.
    enterLookBook(details, groups);
    try {
      const sessionId = await ensureCollectionBuildSessionId(
        persist,
        sessionParam ||
          hydratedFormSessionIdRef.current ||
          peekCollectionBuildDraftSessionId()
      );
      hydratedFormSessionIdRef.current = sessionId;
      rememberCollectionBuildDraftSessionId(sessionId);
      refreshSummaryFromForm(sessionId, "generating");
      void refreshSummaries();
      setSearchParams(collectionBuildSessionSearchParams(sessionId), {
        replace: true,
      });
      // Watch only after enqueue — same race as moodboard (empty session GET).
      const result = await generateCollectionBuildLooks({
        ...persist,
        sessionId,
        looks,
      });
      const assembledPrefs = preferencesFromCollectionRecords(
        result.items || []
      );
      if (assembledPrefs.length) {
        setResultDetails(prev =>
          prev
            ? enrichCollectionBuildDetails(
                mergeCollectionBuildDetailsPreferences(prev, assembledPrefs),
                catalog,
                denimCatalogForDetails
              )
            : prev
        );
      }
      const nextSessionId = result.sessionId || sessionId;
      hydratedFormSessionIdRef.current = nextSessionId;
      rememberCollectionBuildDraftSessionId(nextSessionId);
      refreshSummaryFromForm(nextSessionId, "generating");
      void refreshSummaries();
      watchSession(nextSessionId);
      if (nextSessionId !== sessionId) {
        setSearchParams(collectionBuildSessionSearchParams(nextSessionId), {
          replace: true,
        });
      }
    } catch (error) {
      const message = getCollectionBuildErrorMessage(
        error,
        error instanceof Error
          ? error.message
          : "Unable to start look generation."
      );
      setEnqueueError(message);
      toast.error(collectionBuildGenerationErrorCopy("look", message).toast);
    } finally {
      setLooksSubmitting(false);
    }
  };

  const handleGenerateLooks = () => {
    if (skipSilhouetteOpen || looksSubmitting || confirmSkipMoodboard) {
      return;
    }
    if (!canGenerateCollection) {
      if (silhouetteUploadIncomplete) {
        toast.error(INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE);
        scrollCollectionSectionIntoView(lookDetailsRef.current, "start");
      } else if (!catalogReadyForGenerate) {
        toast.error(
          catalogError
            ? "Catalog is not loaded. Sign in and retry before generating looks."
            : "No extracted reports for these filters."
        );
      } else if (!denimCatalogReady) {
        toast.error(
          denimCatalog.denimError
            ? "Denim catalog is not loaded. Sign in and retry before generating looks."
            : "Select denim material and wash on each style before generating looks."
        );
      }
      return;
    }
    if (!hasRangePlanQuantity) {
      toast.error("Add at least one product with a quantity greater than 0.");
      return;
    }
    if (
      !areSilhouetteGroupsComplete(draftLookGroups, {
        category,
        market,
        startingDirection,
      })
    ) {
      toast.error(
        "Complete Look Details (Select Product) before generating looks."
      );
      scrollCollectionSectionIntoView(lookDetailsRef.current, "start");
      return;
    }
    setConfirmSkipMoodboard(true);
  };

  const handleConfirmSkipMoodboard = () => {
    if (looksSubmitting) {
      return;
    }
    // Go straight to Results (looks busy), not Look Details.
    setConfirmSkipMoodboard(false);
    setSkipSilhouetteOpen(false);
    setPendingSkipDetails(null);
    void handleSkipGenerateLook(draftLookGroups);
  };

  const handleBackToQuery = () => {
    setStayOnQuery(true);
    setActiveTab("query");
    setEnqueueError(null);
    setResultDetails(null);
    setLookGroups(undefined);
    setMoodboardSubmitting(false);
    setLooksSubmitting(false);
    if (!market || !category) {
      setIsHydratingSession(true);
    }
  };

  // IG sessionLocked parity: keep query form mounted above results, muted + non-interactive.
  const queryLocked = Boolean(resultDetails && !stayOnQuery);

  useEffect(() => {
    if (activeTab !== "tracker") {
      return;
    }
    void refreshSummaries();
  }, [activeTab, refreshSummaries]);

  return (
    <div
      ref={pageRef}
      className={`${
        !isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"
      } ${pageShellClassName}`}
    >
      <div className="w-full pb-16">
        <div className="mb-3 flex items-center text-xl sm:text-[22px]">
          <button
            type="button"
            onClick={() => setActiveTab("query")}
            className={`pr-3 font-semibold ${
              activeTab === "query" ? "text-secondary" : "text-tertiary"
            }`}
          >
            Report Query
          </button>
          <span className="font-semibold text-secondary">|</span>
          <button
            type="button"
            onClick={() => setActiveTab("tracker")}
            className={`px-3 font-semibold ${
              activeTab === "tracker" ? "text-secondary" : "text-tertiary"
            }`}
          >
            Report Tracker
          </button>
        </div>

        {activeTab === "query" ? (
          <p className="mb-5 text-sm text-secondary">
            Hey! I’m VYRA, your creative co-pilot. Let’s design your next
            on-trend collection
          </p>
        ) : null}

        {activeTab === "tracker" ? (
          <CollectionBuildTracker
            onOpen={sessionId => {
              setActiveTab("query");
              openSession(sessionId);
            }}
          />
        ) : isHydratingSession && !queryLocked ? (
          <div
            className="flex w-full min-h-[calc(100vh-11rem)] flex-col items-center justify-center rounded-2xl border border-light bg-[#D3C0B626] px-6 py-20"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-[#D3C0B6] border-t-transparent"
              aria-hidden
            />
            <p className="mt-4 font-avenir text-sm font-medium text-secondary">
              Loading session…
            </p>
          </div>
        ) : (
          <>
            <div
              className={
                queryLocked ? "pointer-events-none select-none opacity-80" : ""
              }
              aria-disabled={queryLocked || undefined}
            >
              <div className={`${sectionBlockClassName} pt-0`}>
                <div className={formFieldsShellClassName}>
                  <h2 className={sectionTitleClassName}>Select Inputs</h2>
                  <div className={fieldsRowClassName}>
                    <div className={FIELD_CONTROL_WIDTH_CLASS}>
                      <span className={fieldLabelClassName}>Genre</span>
                      <CollectionBuildSelect
                        value={LOCKED_GENRE}
                        placeholder="Select"
                        options={[{ value: LOCKED_GENRE, label: LOCKED_GENRE }]}
                        onChange={() => undefined}
                        disabled
                      />
                    </div>
                    <div className={FIELD_CONTROL_WIDTH_CLASS}>
                      <span className={fieldLabelClassName}>
                        Market
                        <RequiredMark />
                      </span>
                      <CollectionBuildSelect
                        value={market}
                        placeholder="Select"
                        options={marketOptions}
                        onChange={setMarket}
                      />
                    </div>
                    <div className={FIELD_CONTROL_WIDTH_CLASS}>
                      <span className={fieldLabelClassName}>
                        Category
                        <RequiredMark />
                      </span>
                      <CollectionBuildSelect
                        value={category}
                        placeholder="Select"
                        options={categoryOptions}
                        onChange={setCategory}
                      />
                    </div>
                    <div className={FIELD_CONTROL_WIDTH_CLASS}>
                      <span className={fieldLabelClassName}>
                        Season
                        <RequiredMark />
                      </span>
                      <CollectionBuildSelect
                        value={seasonId}
                        placeholder={
                          !seasonEnabled
                            ? "Select"
                            : seasonsLoading
                              ? "Loading…"
                              : seasonsError
                                ? "Unable to load seasons"
                                : "Select"
                        }
                        options={seasonOptions}
                        onChange={setSeasonId}
                        disabled={!seasonEnabled || seasonsLoading}
                      />
                      {seasonsError ? (
                        <span className="text-xs text-negative">
                          Sign in and retry — seasons come from the live
                          catalog.
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {inputsComplete && (
                <>
                  <CollectionBuildRangePlan
                    items={rangePlan}
                    productOptions={productSelectOptions}
                    canAdd={canAddProduct}
                    onChange={updateRangePlan}
                    onAdd={handleAddProduct}
                    onRemove={handleRemoveProduct}
                  />

                  <CollectionBuildDirections
                    startingDirection={startingDirection}
                    macroTheme={macroTheme}
                    selectedPaletteIds={selectedPaletteIds}
                    extraPalettes={extraPalettes}
                    catalogPalettes={catalogPalettes}
                    macroOptions={macroOptions}
                    microOptions={microOptions}
                    preferenceGroups={preferenceGroups}
                    trendSource={trendSource}
                    surfaceSlides={surfaceSlides}
                    surfacesLoading={surfacesLoading}
                    selectedSurfaceIds={selectedSurfaceIds}
                    patternChoice={patternChoice}
                    activeMicroTheme={activeMicroTheme}
                    selectedMicroThemes={selectedMicroThemes}
                    selectedPreferenceIds={selectedPreferenceIds}
                    patternStoryOptions={patternStoryOptions}
                    activePatternStoryId={activePatternStoryId}
                    selectedPatternStoryIds={selectedPatternStoryIds}
                    macroReportSlides={macroReportSlides}
                    patternTrendReportSlides={patternTrendReportSlides}
                    isWesternPatternFlow={isWesternPatternFlow}
                    showDirection={showDirection}
                    directionOptions={directionOptions}
                    enabledDirections={availableDirections}
                    directionMessage={directionMessage}
                    showMacro={showMacro}
                    themeSectionTitle={
                      isCapsulesPath ? "Select Capsule" : "Select Macro Theme"
                    }
                    themeEmptyMessage={
                      isCapsulesPath
                        ? "No capsules for these filters yet."
                        : "No macro themes for these filters yet."
                    }
                    showPalette={showPalette}
                    showTrendSource={showTrendSource}
                    showSurfaceSlides={showSurfaceSlides}
                    showPattern={showPattern}
                    showMicro={showMicro}
                    showPatternStories={showPatternStories}
                    showPreferences={showPreferences}
                    showMoodboard={showMoodboard}
                    showGenerateActions={!queryLocked}
                    additionalPrompt={additionalPrompt}
                    catalogMessage={catalogMessage}
                    onStartingDirection={handleStartingDirection}
                    onMacroTheme={handleMacroTheme}
                    onTogglePalette={handleTogglePalette}
                    onAddPalette={handleAddPalette}
                    onTrendSource={handleTrendSource}
                    onToggleSurfaceId={handleToggleSurfaceId}
                    onPatternChoice={handlePatternChoice}
                    onSelectMicroTheme={handleSelectMicroTheme}
                    onSelectPatternStory={handleSelectPatternStory}
                    onTogglePreference={handleTogglePreference}
                    onAdditionalPromptChange={setAdditionalPrompt}
                    onGenerateMoodboard={() => {
                      if (!canGenerateCollection) {
                        if (silhouetteUploadIncomplete) {
                          toast.error(INCOMPLETE_SILHOUETTE_UPLOAD_MESSAGE);
                          scrollCollectionSectionIntoView(
                            lookDetailsRef.current,
                            "start"
                          );
                        } else if (!catalogReadyForGenerate) {
                          toast.error(
                            catalogError
                              ? "Catalog is not loaded. Sign in and retry."
                              : "Select a catalog colour story before generating."
                          );
                        } else if (!denimCatalogReady) {
                          toast.error(
                            "Select denim material and wash on each style before generating."
                          );
                        }
                        return;
                      }
                      setConfirmSkipMoodboard(false);
                      void handleGenerateMoodboard();
                    }}
                    onGenerateLooks={handleGenerateLooks}
                    afterPreferences={
                      showLookDetails ? (
                        <CollectionBuildSilhouette
                          ref={lookDetailsRef}
                          breakdown={lookDetailsBreakdown}
                          category={category}
                          market={market}
                          season={seasonLabel}
                          seasonId={seasonId}
                          startingDirection={startingDirection}
                          deferredGenerate
                          seedLooks={seedLooks}
                          sessionGarmentFallback={{
                            garmentFormat,
                            patternFormat,
                            embroideryType,
                          }}
                          onGroupsChange={handleDraftLookGroupsChange}
                          showGarmentFormat={showLookGarmentFormat}
                          denimMaterialOptions={denimCatalog.materials.map(
                            item => ({
                              id: item.id,
                              label: item.label,
                              previewUrl:
                                denimPreviews[item.id] ||
                                (/^https?:\/\//i.test(item.url)
                                  ? item.url
                                  : undefined),
                            })
                          )}
                          denimWashOptions={denimCatalog.washes.map(item => ({
                            id: item.id,
                            label: item.label,
                            previewUrl:
                              denimPreviews[item.id] ||
                              (/^https?:\/\//i.test(item.url)
                                ? item.url
                                : undefined),
                          }))}
                          generating={looksSubmitting}
                          onGenerateLook={() => undefined}
                        />
                      ) : null
                    }
                    actionsLocked={
                      queryLocked ||
                      skipSilhouetteOpen ||
                      !canGenerateCollection
                    }
                  />
                </>
              )}
            </div>

            {queryLocked && resultDetails ? (
              <CollectionBuildResultsBoundary
                resetKey={sessionParam || "draft"}
                onBackToQuery={handleBackToQuery}
              >
                <CollectionBuildResults
                  details={resultDetails}
                  sessionId={sessionParam}
                  initialSilhouetteGroups={lookGroups}
                  enqueueError={enqueueError}
                  onBackToQuery={handleBackToQuery}
                />
              </CollectionBuildResultsBoundary>
            ) : null}
          </>
        )}
      </div>
      {confirmSkipMoodboard &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center font-avenir">
            <div
              className="fixed inset-0 bg-black opacity-50"
              onClick={() => setConfirmSkipMoodboard(false)}
            />
            <div
              className={`relative z-10 max-h-[min(32rem,calc(100vh-2rem))] w-full max-w-sm overflow-y-auto rounded-2xl border ${collectionBuildBorderClassName} bg-white p-6 shadow-lg`}
            >
              <h2 className="text-xl font-semibold text-primary">
                {SKIP_MOODBOARD_CONFIRM_TITLE}
              </h2>
              <p className="mt-2 text-sm text-secondary">
                {SKIP_MOODBOARD_CONFIRM_BODY}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmSkipMoodboard(false)}
                  className={outlineButtonClassName}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSkipMoodboard}
                  disabled={looksSubmitting}
                  className={`${submitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {looksSubmitting ? "Starting…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CollectionBuild;
